// Session lifetime rules for shared terminals.
//
// Two separate things end a session:
//   1. closing the browser  — reopening starts signed out
//   2. sitting idle         — a badge left signed in on a warehouse bench
//                             shouldn't stay open all night
//
// The token stays in localStorage because 37 files read it from there; this
// module gates it on boot instead of rewriting every call site.

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

// Present only for the life of a tab. Cleared by the browser when the tab or
// window closes, which is what lets us tell "reopened" from "reloaded".
const TAB_MARKER = 'optivaTabAlive';

// Shared across tabs, so a second tab opening doesn't look like a cold start
// and sign the first one out.
const LAST_SEEN_KEY = 'optivaLastSeen';
const LAST_ACTIVITY_KEY = 'optivaLastActivity';

// If no tab has checked in for longer than this, every window was closed.
// Comfortably longer than the heartbeat, short enough that a genuine restart is
// never mistaken for a live session.
const COLD_START_GRACE_MS = 45_000;
export const HEARTBEAT_MS = 10_000;

// Idle limit. One constant — change it here and both the countdown and the
// sign-out follow.
export const IDLE_LIMIT_MS = 20 * 60 * 1000;   // 20 minutes
export const IDLE_WARN_MS = 60 * 1000;         // final minute is a warning

const read = (store, key) => {
  try { return store.getItem(key); } catch { return null; }
};
const write = (store, key, value) => {
  try { store.setItem(key, value); } catch { /* private mode / quota */ }
};
const drop = (store, key) => {
  try { store.removeItem(key); } catch { /* ignore */ }
};

export const clearAuth = () => {
  drop(window.localStorage, TOKEN_KEY);
  drop(window.localStorage, USER_KEY);
  drop(window.localStorage, LAST_ACTIVITY_KEY);
  drop(window.localStorage, LAST_SEEN_KEY);
};

export const hasSession = () => Boolean(
  read(window.localStorage, TOKEN_KEY) && read(window.localStorage, USER_KEY)
);

export const markActivity = () => write(window.localStorage, LAST_ACTIVITY_KEY, String(Date.now()));
export const heartbeat = () => write(window.localStorage, LAST_SEEN_KEY, String(Date.now()));

export const msUntilIdleLogout = () => {
  const last = Number(read(window.localStorage, LAST_ACTIVITY_KEY)) || 0;
  if (!last) return IDLE_LIMIT_MS;
  return IDLE_LIMIT_MS - (Date.now() - last);
};

/**
 * Run once, before the app renders.
 *
 * Clears the stored session when the browser was closed and reopened. A plain
 * "no tab marker means cold start" test would sign you out every time you
 * opened a second tab — localStorage is shared, so that would take the first
 * tab down too. The heartbeat distinguishes the two: if any window checked in
 * moments ago, this is a new tab beside a live one, not a restart.
 *
 * Caveat worth knowing: browsers set to restore tabs on startup also restore
 * sessionStorage, so a restored tab can look like a reload. The idle timer is
 * the backstop for that case.
 */
export const enforceFreshBrowserSession = () => {
  const tabAlive = read(window.sessionStorage, TAB_MARKER);
  if (!tabAlive) {
    const lastSeen = Number(read(window.localStorage, LAST_SEEN_KEY)) || 0;
    const anotherTabIsLive = lastSeen && (Date.now() - lastSeen) < COLD_START_GRACE_MS;
    if (!anotherTabIsLive && hasSession()) {
      clearAuth();
    }
    write(window.sessionStorage, TAB_MARKER, '1');
  }
  heartbeat();
  if (hasSession() && !read(window.localStorage, LAST_ACTIVITY_KEY)) markActivity();
};
